import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import type { DocumentRequirementView, ProgramVersionView } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { DocumentRequirementsEditor, RuleEditor } from '../components/rule-editor'
import { describeRule, type RuleDraft, ruleDraftFrom, RuleDraftError, toRules } from '../lib/rule-form'
import { Alert, Button, Card, Field, Input, Mono, Row, Select } from '../components/ui'
import { errorMessage } from '../lib/api'
import { formatDate, formatDay, humanize } from '../lib/format'
import { useNewProgramVersion, usePatchProgram, useProgram, useProgramCommand } from '../lib/programs'
import { isTexStaff, useMe } from '../lib/session'
import { ProgramStatusBadge } from './partner-detail'

export function ProgramDetailPage({ programId }: { programId: string }) {
  const me = useMe()
  const program = useProgram(programId)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !isTexStaff(me.data)) return <Alert tone="error">Only Tex staff can see partner programs.</Alert>
  if (program.isPending || !me.data) return <p className="text-sm text-neutral-500">Loading…</p>
  if (program.isError) return <Alert tone="error">{errorMessage(program.error)}</Alert>

  const pv = program.data.program
  const isOps = me.data.tex_role === 'ops_admin'
  const editable = isOps && pv.status === 'draft'

  return (
    <section className="space-y-6">
      <div>
        <Link to="/tex/partners/$partnerId" params={{ partnerId: pv.partner_id }} className="text-sm text-neutral-500 hover:text-neutral-800">
          ← Partner
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
          <Mono>{pv.program_code}</Mono> v{pv.version}
          <ProgramStatusBadge status={pv.status} />
        </h1>
        <p className="mt-1 text-sm text-neutral-500">{pv.title}</p>
      </div>

      {pv.status !== 'draft' && (
        <Alert tone="info">
          This version is {humanize(pv.status)} and frozen: rules, document requirements and dates cannot change. Create a new version to edit; activating it
          retires the current active version.
        </Alert>
      )}

      <Card title="Lifecycle">
        <dl>
          <Row
            label="Effective"
            value={pv.effective_from ? `${formatDay(pv.effective_from)} → ${pv.effective_to ? formatDay(pv.effective_to) : 'open'}` : 'not set'}
          />
          <Row
            label="Criteria verified"
            value={pv.criteria_verified_at ? formatDate(pv.criteria_verified_at) : 'unverified — evaluations return manual review'}
          />
          <Row label="Unknown-fact policy" value={humanize(pv.unknown_policy)} />
          <Row label="Created" value={formatDate(pv.created_at)} />
        </dl>
        {isOps && <LifecycleActions program={pv} />}
      </Card>

      {editable ? <DefinitionEditor key={pv.updated_at} program={pv} /> : <DefinitionReadOnly program={pv} />}
    </section>
  )
}

function LifecycleActions({ program }: { program: ProgramVersionView }) {
  const navigate = useNavigate()
  const command = useProgramCommand(program)
  const newVersion = useNewProgramVersion(program)
  const busy = command.isPending || newVersion.isPending
  const error = command.error ?? newVersion.error
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
      {program.status === 'draft' && (
        <Button variant="secondary" disabled={busy} onClick={() => command.mutate({ command: 'mark_test_only', ...(program.effective_from ? {} : { effective_from: new Date().toISOString().slice(0, 10) }) })}>
          Mark test-only
        </Button>
      )}
      {(program.status === 'draft' || program.status === 'test_only') && (
        <Button
          disabled={busy}
          title="Confirms the criteria were verified against partner documentation now"
          onClick={() =>
            command.mutate({
              command: 'activate',
              criteria_verified_at: new Date().toISOString(),
              ...(program.effective_from || program.status !== 'draft' ? {} : { effective_from: new Date().toISOString().slice(0, 10) }),
            })
          }
        >
          Activate (criteria verified now)
        </Button>
      )}
      {(program.status === 'test_only' || program.status === 'active') && (
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() =>
            newVersion.mutate(
              {},
              {
                onSuccess: (r) =>
                  void navigate({
                    to: '/tex/programs/$programId',
                    params: { programId: r.program.id },
                  }),
              },
            )
          }
        >
          New version from this one
        </Button>
      )}
      {(program.status === 'test_only' || program.status === 'active') && (
        <Button variant="danger" disabled={busy} onClick={() => command.mutate({ command: 'retire' })}>
          Retire
        </Button>
      )}
      {error && <span className="text-xs text-tex-red">{errorMessage(error)}</span>}
    </div>
  )
}

function DefinitionReadOnly({ program }: { program: ProgramVersionView }) {
  return (
    <>
      <Card title={`Rules (${program.rules.length})`}>
        {program.rules.length === 0 ? (
          <p className="text-sm text-neutral-500">No rules.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {program.rules.map((r, i) => (
              <li key={i}>{describeRule(r)}</li>
            ))}
          </ul>
        )}
      </Card>
      <Card title={`Document requirements (${program.document_requirements.length})`}>
        <DocumentRequirementsEditor items={program.document_requirements} onChange={() => undefined} disabled />
      </Card>
    </>
  )
}

function DefinitionEditor({ program }: { program: ProgramVersionView }) {
  const patch = usePatchProgram(program)
  const [title, setTitle] = useState(program.title)
  const [unknownPolicy, setUnknownPolicy] = useState(program.unknown_policy === 'auto_decline' ? 'auto_decline' : 'manual_review')
  const [effectiveFrom, setEffectiveFrom] = useState(program.effective_from ?? '')
  const [effectiveTo, setEffectiveTo] = useState(program.effective_to ?? '')
  const [rules, setRules] = useState<RuleDraft[]>(program.rules.map(ruleDraftFrom))
  const [docs, setDocs] = useState<DocumentRequirementView[]>(program.document_requirements)
  const [draftError, setDraftError] = useState<RuleDraftError | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    setDraftError(null)
    let compiled
    try {
      compiled = toRules(rules)
    } catch (err) {
      if (err instanceof RuleDraftError) return setDraftError(err)
      throw err
    }
    patch.mutate({
      title: title.trim(),
      unknown_policy: unknownPolicy === 'auto_decline' ? 'auto_decline' : 'manual_review',
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      rules: compiled,
      document_requirements: docs.map((d) => ({
        ...d,
        document_type: d.document_type.trim(),
      })),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card title="Definition (draft)">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Title">
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field
            label="Unknown-fact policy"
            hint="What happens when a rule touches a fact the case has not collected. Either way the case never passes on unknown data."
          >
            <Select value={unknownPolicy} onChange={(e) => setUnknownPolicy(e.target.value)}>
              <option value="manual_review">manual review</option>
              <option value="auto_decline">auto decline</option>
            </Select>
          </Field>
          <Field label="Effective from" hint="Required before activation; frozen afterwards.">
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Effective to" hint="Leave blank for an open-ended program.">
            <Input type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
          </Field>
        </div>
      </Card>
      <Card title={`Rules (${rules.length})`}>
        <p className="mb-3 text-xs text-neutral-500">
          Every rule must hold for a case to be eligible. Facts are drawn from a closed allowlist; a rule over an unknown fact sends the case to manual review,
          never to eligible. Jurisdiction checks run before these rules.
        </p>
        <RuleEditor drafts={rules} onChange={setRules} disabled={false} errorIndex={draftError?.index} />
      </Card>
      <Card title={`Document requirements (${docs.length})`}>
        <p className="mb-3 text-xs text-neutral-500">Recorded as the partner states them. Unknown requirements stay unrecorded rather than guessed.</p>
        <DocumentRequirementsEditor items={docs} onChange={setDocs} disabled={false} />
      </Card>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={patch.isPending}>
          Save draft
        </Button>
        {draftError && <Alert tone="error">{draftError.message}</Alert>}
        {patch.isError && <Alert tone="error">{errorMessage(patch.error)}</Alert>}
        {patch.isSuccess && !patch.isPending && <Alert tone="success">Saved.</Alert>}
      </div>
    </form>
  )
}
